import { ReactNode } from "react"
import { ChevronUp, Hand, MessageSquareText, Mic, MicOff, MonitorUp, MoreVertical, PanelRightOpen, PhoneOff, SmilePlus, Video, VideoOff } from "lucide-react"

type ControlsBarProps = {
  micOn: boolean
  camOn: boolean
  canSendAudio: boolean
  canSendVideo: boolean
  onToggleMic: () => void
  onToggleCam: () => void
  /** The chevron beside the microphone: quick microphone/speaker choice plus a settings cog. */
  micMenuOpen: boolean
  onToggleMicMenu: () => void
  /** Shown on the microphone chevron until it is hovered or open (Meet's live waveform). */
  micIndicator?: ReactNode
  /** The chevron beside the camera: quick camera choice, blur, effects, plus a settings cog. */
  camMenuOpen: boolean
  onToggleCamMenu: () => void
  canShareScreen: boolean
  isSharingScreen: boolean
  onToggleScreenShare: () => void
  handRaised: boolean
  onToggleHand: () => void
  reactionsOpen: boolean
  onToggleReactions: () => void
  /** In-call chat lives in the bar (the owner wanted it beside the other call controls). */
  chatOpen: boolean
  chatUnreadCount: number
  onToggleChat: () => void
  /** The meeting tools panel (meeting details, People, Questions, Breakout, Inspire); the header has the same toggle. */
  toolsOpen: boolean
  toolsBadge: number
  onToggleTools: () => void
  moreOpen: boolean
  onToggleMore: () => void
  onLeave: () => void
  compact: boolean
  /** Phones move Send a reaction into the More menu so the bar fits without scrolling sideways. */
  mobile?: boolean
}

/**
 * Height the floating controls need at the bottom of the stage. The stage reserves it so tiles,
 * name labels, and the thumbnail rail never sit under the buttons (Daily Prebuilt keeps a tray
 * row for the same reason); the layout QA at 844×390 and 390×844 showed them covered before.
 */
export const CONTROLS_BAR_RESERVED_SPACE = "calc(4rem + max(0.75rem, env(safe-area-inset-bottom)))"

export const canShareScreenInThisBrowser = () =>
  typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getDisplayMedia === "function"

export function ControlButton({
  label,
  active,
  danger,
  disabled,
  expanded,
  onClick,
  children,
  testId,
  className = "",
}: {
  label: string
  active?: boolean
  danger?: boolean
  disabled?: boolean
  /** For buttons that open a menu (aria-expanded). */
  expanded?: boolean
  onClick: () => void
  children: ReactNode
  testId?: string
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      aria-expanded={expanded}
      title={label}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-40 sm:h-12 sm:w-12 ${
        danger
          ? "bg-[#d64545] text-white hover:bg-[#c03a3a]"
          : active
            ? "bg-white text-[#1c2530] hover:bg-white/90"
            : "bg-white/10 text-white hover:bg-white/20"
      } ${className}`}
    >
      {children}
    </button>
  )
}

/**
 * A media control with a chevron beside it, as in Google Meet: the chevron opens a small menu
 * with the device choice and a settings cog, the main button toggles the device.
 */
function SplitControl({
  label,
  active,
  disabled,
  onClick,
  testId,
  menuLabel,
  menuOpen,
  onToggleMenu,
  menuTestId,
  showChevron,
  restIcon,
  children,
}: {
  label: string
  active: boolean
  disabled: boolean
  onClick: () => void
  testId: string
  menuLabel: string
  menuOpen: boolean
  onToggleMenu: () => void
  menuTestId: string
  showChevron: boolean
  /** Replaces the chevron while the control is idle; the chevron shows on hover, focus, or when the menu is open. */
  restIcon?: ReactNode
  children: ReactNode
}) {
  if (!showChevron) {
    return (
      <ControlButton label={label} active={active} disabled={disabled} onClick={onClick} testId={testId}>
        {children}
      </ControlButton>
    )
  }
  return (
    <div className={`flex items-center rounded-full ${active ? "bg-white" : "bg-white/10"}`}>
      <button
        type="button"
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={menuLabel}
        onClick={onToggleMenu}
        data-testid={menuTestId}
        className={`group flex h-11 w-7 items-center justify-center rounded-l-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:h-12 sm:w-8 ${
          active ? "text-[#1c2530] hover:bg-black/10" : "text-white hover:bg-white/20"
        } ${menuOpen ? (active ? "bg-black/10" : "bg-white/20") : ""}`}
      >
        {restIcon && !menuOpen && <span className="group-hover:hidden group-focus-visible:hidden">{restIcon}</span>}
        <ChevronUp className={`h-4 w-4 transition-transform ${menuOpen ? "rotate-180" : ""} ${restIcon && !menuOpen ? "hidden group-hover:block group-focus-visible:block" : ""}`} />
      </button>
      <button
        type="button"
        aria-label={label}
        aria-pressed={active}
        title={label}
        disabled={disabled}
        onClick={onClick}
        data-testid={testId}
        className={`flex h-11 w-11 items-center justify-center rounded-r-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-40 sm:h-12 sm:w-12 ${
          active ? "text-[#1c2530] hover:bg-black/10" : "text-white hover:bg-white/20"
        }`}
      >
        {children}
      </button>
    </div>
  )
}

/**
 * In-call controls for the custom surface, laid out like Google Meet's bar: microphone and
 * camera (each with a chevron for quick device choices), share, reactions, raise hand, a
 * Chat, the meeting Tools panel, a three-dot menu (layout, full screen, picture-in-picture,
 * effects, settings), and leave.
 * Fixed to the bottom of the stage with safe-area padding so it stays reachable on phones.
 */
export function ControlsBar({
  micOn,
  camOn,
  canSendAudio,
  canSendVideo,
  onToggleMic,
  onToggleCam,
  micMenuOpen,
  onToggleMicMenu,
  micIndicator,
  camMenuOpen,
  onToggleCamMenu,
  canShareScreen,
  isSharingScreen,
  onToggleScreenShare,
  handRaised,
  onToggleHand,
  reactionsOpen,
  onToggleReactions,
  chatOpen,
  chatUnreadCount,
  onToggleChat,
  toolsOpen,
  toolsBadge,
  onToggleTools,
  moreOpen,
  onToggleMore,
  onLeave,
  compact,
  mobile = false,
}: ControlsBarProps) {
  return (
    <div
      data-testid="call-controls"
      className="pointer-events-none absolute inset-x-0 bottom-0 z-[40] flex justify-center px-3"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="pointer-events-auto flex max-w-full items-center gap-1.5 overflow-x-auto sm:gap-2 rounded-full bg-[#0f1419]/85 px-2 py-2 shadow-2xl ring-1 ring-white/10 backdrop-blur">
        <SplitControl
          label={micOn ? "Mute microphone" : "Unmute microphone"}
          active={micOn}
          disabled={!canSendAudio}
          onClick={onToggleMic}
          testId="call-toggle-mic"
          menuLabel="Microphone options"
          menuOpen={micMenuOpen}
          onToggleMenu={onToggleMicMenu}
          menuTestId="call-mic-options"
          showChevron={!compact}
          restIcon={micIndicator}
        >
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </SplitControl>
        <SplitControl
          label={camOn ? "Turn camera off" : "Turn camera on"}
          active={camOn}
          disabled={!canSendVideo}
          onClick={onToggleCam}
          testId="call-toggle-cam"
          menuLabel="Camera options"
          menuOpen={camMenuOpen}
          onToggleMenu={onToggleCamMenu}
          menuTestId="call-cam-options"
          showChevron={!compact}
        >
          {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </SplitControl>
        {canShareScreen && (
          <ControlButton label={isSharingScreen ? "Stop sharing screen" : "Share screen"} active={isSharingScreen} onClick={onToggleScreenShare} testId="call-toggle-share">
            <MonitorUp className="h-5 w-5" />
          </ControlButton>
        )}
        {!mobile && (
          <ControlButton label="Send a reaction" expanded={reactionsOpen} active={reactionsOpen} onClick={onToggleReactions} testId="call-open-reactions">
            <SmilePlus className="h-5 w-5" />
          </ControlButton>
        )}
        <ControlButton label={handRaised ? "Lower hand" : "Raise hand"} active={handRaised} onClick={onToggleHand} testId="call-toggle-hand">
          <Hand className="h-5 w-5" />
        </ControlButton>
        <ControlButton label={chatUnreadCount > 0 ? `Chat, ${chatUnreadCount} unread` : "Chat"} active={chatOpen} expanded={chatOpen} onClick={onToggleChat} testId="call-toggle-chat" className="relative">
          <MessageSquareText className="h-5 w-5" />
          {chatUnreadCount > 0 && (
            <span data-testid="call-chat-unread" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d64545] px-1 text-[10px] font-bold text-white ring-2 ring-[#0f1419]">
              {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
            </span>
          )}
        </ControlButton>
        <ControlButton label={toolsOpen ? "Hide meeting tools" : toolsBadge > 0 ? `Meeting tools, ${toolsBadge} unread` : "Meeting tools"} active={toolsOpen} expanded={toolsOpen} onClick={onToggleTools} testId="call-toggle-tools" className="relative">
          <PanelRightOpen className="h-5 w-5" />
          {toolsBadge > 0 && (
            <span data-testid="call-tools-unread" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#d64545] px-1 text-[10px] font-bold text-white ring-2 ring-[#0f1419]">
              {toolsBadge > 99 ? "99+" : toolsBadge}
            </span>
          )}
        </ControlButton>
        <ControlButton label="More options" expanded={moreOpen} active={moreOpen} onClick={onToggleMore} testId="call-more-options">
          <MoreVertical className="h-5 w-5" />
        </ControlButton>
        <ControlButton label="Leave meeting" danger onClick={onLeave} testId="call-leave">
          <PhoneOff className="h-5 w-5" />
        </ControlButton>
      </div>
    </div>
  )
}
